import { z } from 'zod';

import { normaliseNameInput } from '@/utils/formatters/string';
import {
  DEFAULT_PHONE_COUNTRY,
  getCountryName,
  isPhoneCountry,
  isPhoneValidForCountry,
} from '@/utils/helpers/phone.helpers';
import {
  lettersAndSpacesOnlyRegex,
  lettersSpacesHyphensApostrophesRegex,
} from '@/utils/regexes/regexes';

/**
 * Validation schema, limits and default values for the contributor details
 * form (GIFTWEB-16 / GIFTWEB-17). Kept apart from the page so the page file
 * only exports a component (fast refresh) and the form doesn't import the page.
 */

const ALLOW_TEST_NUMBERS = import.meta.env.VITE_ALLOW_TEST_NUMBERS === 'true';

export const NAME_MAX_LENGTH =
  Number(import.meta.env.VITE_MV_NAME_MAX_LENGTH) || 20;
export const NAME_MIN_LENGTH =
  Number(import.meta.env.VITE_MV_NAME_MIN_LENGTH) || 2;
export const MAX_EMAIL_LENGTH =
  Number(import.meta.env.VITE_MV_MAX_EMAIL_LENGTH) || 254;
export const MAX_PHONE_LENGTH =
  Number(import.meta.env.VITE_MV_MAX_PHONE_LENGTH) || 25;

export const defaultValues = {
  legalFirstName: '',
  legalLastName: '',
  email: '',
  phone: '',
  phoneCountry: DEFAULT_PHONE_COUNTRY as string,
  relationshipToChild: '',
  nameKnownAs: '',
};

// Empty required fields are surfaced as the text-free "nudge" (AC 4), so the
// `required` messages below are only ever read by assistive tech / tests.
const nameSchema = (label: string, pattern: RegExp, patternMessage: string) =>
  z
    .string()
    .min(1, { message: `${label} is required` })
    .max(NAME_MAX_LENGTH, {
      message: `${label} must be ${NAME_MAX_LENGTH} characters or less`,
    })
    .transform(normaliseNameInput)
    .refine((value) => pattern.test(value), patternMessage)
    .refine(
      (value) => value.trim().length >= NAME_MIN_LENGTH,
      `${label} must be at least ${NAME_MIN_LENGTH} characters`,
    );

export const UserSchema = z.object({
  legalFirstName: nameSchema(
    'Legal first name',
    lettersAndSpacesOnlyRegex,
    'Legal first name can only contain letters and spaces',
  ),
  legalLastName: nameSchema(
    'Legal last name',
    lettersSpacesHyphensApostrophesRegex,
    'Legal last name can only contain letters, spaces, hyphens, and apostrophes',
  ),
  email: z
    .string()
    .min(1, { message: 'Email address is required' })
    .max(MAX_EMAIL_LENGTH, {
      message: `Email address must be ${MAX_EMAIL_LENGTH} characters or less`,
    })
    .pipe(z.email({ message: 'Please enter a valid email address' })),
  // Optional; validated against the selected country below (GIFTWEB-17).
  phone: z.string().max(MAX_PHONE_LENGTH, {
    message: `Mobile number must be ${MAX_PHONE_LENGTH} characters or less`,
  }),
  phoneCountry: z
    .string()
    .refine((value): boolean => isPhoneCountry(value), 'Please choose a country'),
  nameKnownAs: z
    .string()
    .max(NAME_MAX_LENGTH, {
      message: `This name must be ${NAME_MAX_LENGTH} characters or less`,
    })
    .transform(normaliseNameInput)
    .refine(
      (value) => lettersSpacesHyphensApostrophesRegex.test(value),
      'This name can only contain letters, spaces, hyphens, and apostrophes',
    )
    .refine(
      (value) => value.trim().length >= NAME_MIN_LENGTH,
      `This name must be at least ${NAME_MIN_LENGTH} characters`,
    )
    .optional()
    .or(z.literal('')),
  relationshipToChild: z
    .string()
    .min(1, { message: 'Relationship is required' }),
}).superRefine((data, ctx) => {
  if (!data.phone.trim() || !isPhoneCountry(data.phoneCountry)) return;
  if (
    !isPhoneValidForCountry(data.phone, data.phoneCountry, {
      allowTestNumbers: ALLOW_TEST_NUMBERS,
    })
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['phone'],
      message: `Please enter a valid mobile number for ${getCountryName(data.phoneCountry)}`,
    });
  }
});

export type FormValues = z.infer<typeof UserSchema>;
